import React from 'react';
import { Wine as WineIcon, MapPin, Star } from 'lucide-react';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { RightPanel } from '@/components/ui/right-panel';
import { Heading, MonoLabel, Body, Badge, Chip, Divider, WineTypeIndicator } from '@/components/rc';
import { ImageWithFallback } from '@/components/rc/figma/ImageWithFallback';
import { useIsMobile } from '@/components/ui/use-mobile';
import { toRCWineCardProps } from '@/lib/adapters';
import { formatGrapeDisplay } from '@/utils/grapeUtils';
import { displayVintage } from '@/utils/vintageUtils';
import { getMaturityLabelFromKebab } from '@/utils/maturityUtils';
import { getDirectImageUrl } from '@/utils/imageUrl';
import { formatPrice } from '@/lib/formatPrice';
import type { SharedWine } from '@/types';
import type { WineType } from '@/components/rc/WineTypeIndicator';

const wineTypeToHeadingColour = {
  red: 'accent-pink',
  white: 'accent-acid',
  rose: 'accent-coral',
  sparkling: 'accent-acid',
  dessert: 'accent-coral',
  orange: 'accent-coral',
} as const;

interface SharedWineDetailProps {
  wine: SharedWine;
  onClose: () => void;
}

const DetailContent: React.FC<{ wine: SharedWine }> = ({ wine }) => {
  const rcProps = toRCWineCardProps(wine as any);
  const vintageColour = wineTypeToHeadingColour[rcProps.type];

  return (
    <>
      {/* Header */}
      <div className="p-6 pb-2 space-y-0.5">
        <div className="flex items-center gap-1.5 opacity-60">
          <MapPin size={10} className="text-[var(--rc-ink-primary)]" />
          <MonoLabel size="micro" colour="primary" uppercase={false} as="span" className="w-auto">
            {wine.region || ''}{wine.region && wine.country ? ', ' : ''}{wine.country || ''}
          </MonoLabel>
        </div>
        <Heading scale="title" truncate>{wine.producer}</Heading>
        <Heading scale="vintage" colour={vintageColour}>{displayVintage(wine)}</Heading>
        <div className="flex items-center gap-2 mt-1">
          <WineTypeIndicator wineType={rcProps.type as WineType} format="pill" />
          <MonoLabel size="micro" weight="bold" colour="ghost">
            {formatGrapeDisplay(wine.grapeVarieties)}
          </MonoLabel>
        </div>
      </div>

      {/* Badges */}
      <div className="px-6 flex flex-wrap items-center gap-3 mb-6">
        {wine.criticRating != null && wine.criticRating > 0 && (
          <div className="flex flex-col items-start gap-0.5 px-3 py-1.5">
            <MonoLabel size="micro" colour="ghost" className="w-auto">Remy</MonoLabel>
            <Badge typeVariant="Rating" label={Number(wine.criticRating).toFixed(1)} />
          </div>
        )}
        {wine.myRating && (
          <div className="flex flex-col items-start gap-0.5 px-3 py-1.5">
            <MonoLabel size="micro" colour="ghost" className="w-auto">My Rating</MonoLabel>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map(s => (
                <Star
                  key={s}
                  size={16}
                  fill={s <= Number(wine.myRating) ? 'var(--rc-accent-pink)' : 'none'}
                  className="text-[var(--rc-accent-pink)]"
                />
              ))}
            </div>
          </div>
        )}
        <Chip
          variant="Maturity"
          state="Selected"
          maturityValue={rcProps.maturity}
          label={getMaturityLabelFromKebab(rcProps.maturity)}
        />
        <div className="flex items-center gap-1 ml-auto px-3 py-1.5 border border-[var(--rc-border-emphasis)] bg-[var(--rc-surface-primary)] rounded-[var(--rc-radius-sm)]">
          <MonoLabel size="micro" weight="bold" colour="ghost" as="span" className="w-auto">Qty</MonoLabel>
          <span className="font-[var(--rc-font-display)] text-2xl leading-none font-black">{wine.quantity}</span>
        </div>
      </div>

      {/* Details */}
      <div className="p-6 pt-0 pb-8">
        <Divider weight="emphasised" className="mb-6" />
        <dl className="grid md:grid-cols-2 gap-3">
          {wine.tastingNotes && <Field label="Tasting Notes" value={wine.tastingNotes} fullWidth />}
          {wine.name && <Field label="Wine Name" value={wine.name} />}
          {wine.format && <Field label="Format" value={wine.format} />}
          {wine.appellation && <Field label="Appellation" value={wine.appellation} />}
          <Field label="Drink From" value={String(wine.drinkFrom || '')} />
          <Field label="Drink Until" value={String(wine.drinkUntil || '')} />
          {wine.price > 0 && (
            <Field label="Bottle Price" value={formatPrice(wine.price, wine.priceCurrency || 'USD')} />
          )}
        </dl>
      </div>
    </>
  );
};

const Field: React.FC<{ label: string; value: string; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div className={`space-y-1 p-[var(--rc-space-md)] border border-[var(--rc-border-emphasis)] bg-[var(--rc-surface-primary)] rounded-[var(--rc-radius-sm)] ${fullWidth ? 'md:col-span-2' : ''}`}>
    <dt><MonoLabel size="micro" weight="bold" colour="accent-pink" as="span" className="w-auto">{label}</MonoLabel></dt>
    <dd><Body size="caption" colour="primary" as="span" className="w-auto">{value}</Body></dd>
  </div>
);

const HeroImage: React.FC<{ wine: SharedWine }> = ({ wine }) => {
  const displayImageUrl = getDirectImageUrl(wine.resolvedImageUrl || wine.imageUrl);
  return (
    <div className="relative h-60 bg-[var(--rc-ink-primary)] flex items-center justify-center overflow-hidden">
      {displayImageUrl ? (
        <ImageWithFallback
          src={displayImageUrl}
          alt={wine.name}
          className="w-full h-full object-contain"
          referrerPolicy="no-referrer"
        />
      ) : (
        <WineIcon size={64} className="text-[var(--rc-ink-on-accent)] opacity-20" />
      )}
    </div>
  );
};

const SharedWineDetail: React.FC<SharedWineDetailProps> = ({ wine, onClose }) => {
  const isMobile = useIsMobile();

  if (!isMobile) {
    return (
      <RightPanel
        open
        onClose={onClose}
        width="detail"
        id="shared-wine-detail"
        title={`${wine.producer} ${displayVintage(wine)}`}
      >
        <HeroImage wine={wine} />
        <DetailContent wine={wine} />
      </RightPanel>
    );
  }

  return (
    <BottomSheet
      open
      onOpenChange={(open) => { if (!open) onClose(); }}
      snapPoint="full"
      snapPoints={['full']}
      id="shared-wine-detail"
      title={`${wine.producer} ${displayVintage(wine)}`}
      dragZone={<HeroImage wine={wine} />}
    >
      <DetailContent wine={wine} />
    </BottomSheet>
  );
};

export default SharedWineDetail;
